import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services/apiInstance';
import { selectMakes } from '../../store/slices/vehicleSlice';

const slugify = (str = '') =>
  String(str).toLowerCase().trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

const POPULAR_MAKES = [
  'Toyota', 'Ford', 'Holden', 'Mazda', 'Nissan', 'Subaru',
  'Hyundai', 'Kia', 'Mitsubishi', 'Honda', 'Volkswagen', 'BMW',
  'Mercedes-Benz', 'Audi', 'Isuzu', 'Dodge', 'Chevrolet', 'Jeep',
  'Land Rover', 'Volvo',
];

// Popular make/model combinations for deeper internal linking (SEO)
const POPULAR_MAKE_MODELS = [
  { make: 'Toyota', models: ['Corolla', 'Camry', 'Hilux', 'RAV4', 'Prado', 'LandCruiser'] },
  { make: 'Ford', models: ['Falcon', 'Focus', 'Ranger', 'Territory', 'Mustang', 'Transit'] },
  { make: 'Holden', models: ['Commodore', 'Cruze', 'Captiva', 'Astra', 'Colorado', 'Ute'] },
  { make: 'Mazda', models: ['3', '6', 'CX-5', 'CX-9', 'BT-50', 'MX-5'] },
  { make: 'Nissan', models: ['Navara', 'Qashqai', 'X-Trail', 'Pathfinder', '370Z', 'Patrol'] },
  { make: 'Hyundai', models: ['i30', 'Elantra', 'Santa Fe', 'Tucson', 'Sonata', 'Veloster'] },
  { make: 'Kia', models: ['Cerato', 'Sportage', 'Sorento', 'Soul', 'Stinger', 'Picanto'] },
  { make: 'Subaru', models: ['Impreza', 'Forester', 'Outback', 'WRX', 'Liberty', 'Levorg'] },
  { make: 'Mitsubishi', models: ['Lancer', 'Outlander', 'ASX', 'Pajero', 'Triton', 'Eclipse'] },
  { make: 'Honda', models: ['Civic', 'Accord', 'CR-V', 'Jazz', 'HR-V', 'City'] },
  { make: 'Volkswagen', models: ['Golf', 'Polo', 'Passat', 'Tiguan', 'Amarok', 'Transporter'] },
  { make: 'Mercedes-Benz', models: ['C-Class', 'E-Class', 'A-Class', 'GLC', 'GLE', 'Sprinter'] },
  { make: 'BMW', models: ['3 Series', '5 Series', 'X3', 'X5', 'X1', '1 Series'] },
  { make: 'Audi', models: ['A3', 'A4', 'A5', 'Q5', 'Q7', 'Q3'] },
  { make: 'Jeep', models: ['Wrangler', 'Grand Cherokee', 'Compass', 'Cherokee', 'Gladiator', 'Renegade'] },
  { make: 'Land Rover', models: ['Range Rover', 'Discovery', 'Defender', 'Evoque', 'Sport', 'Velar'] },
  { make: 'Lexus', models: ['RX', 'NX', 'ES', 'IS', 'UX', 'LX'] },
  { make: 'Isuzu', models: ['D-Max', 'MU-X'] },
  { make: 'Suzuki', models: ['Swift', 'Vitara', 'Jimny', 'Baleno', 'S-Cross', 'Ignis'] },
  { make: 'Renault', models: ['Megane', 'Koleos', 'Captur', 'Master', 'Trafic', 'Kangoo'] },
  { make: 'Tesla', models: ['Model 3', 'Model Y', 'Model S', 'Model X'] },
  { make: 'Volvo', models: ['XC60', 'XC90', 'S60', 'V60', 'XC40', 'S90'] },
  { make: 'Skoda', models: ['Octavia', 'Kodiaq', 'Superb', 'Fabia', 'Karoq', 'Scala'] },
  { make: 'BYD', models: ['Atto 3', 'Seal', 'Dolphin', 'Sealion 6', 'Han', 'Tang'] },
  { make: 'LDV', models: ['T60', 'D90', 'G10', 'Mifa', 'Deliver 9'] },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isMobileNamespace = (location?.pathname || '').startsWith('/mobile');
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const allMakes = useSelector(selectMakes);

  const footerLinks = {
    'Shop': [
      { name: 'Find Your Vehicle', href: '/makes' },
      { name: 'Auto Glass Finder', href: '/parts-finder' },
      { name: 'Search by VIN', href: '/autoglass-vin-search' },
      { name: 'Popular Makes', href: '/makes' },
      { name: 'Accessories', href: '/accessories-supplies' },
    ],
    'Support': [
      { name: 'Contact Us', href: '/contact' },
      { name: 'Returns & Warranty', href: '/pages/returns-and-warranty' },
    ],
    'Company': [
      { name: 'About Us', href: '/pages/about-us' },
      { name: 'Careers', href: '/pages/careers' },
    ],
    'Legal': [
      { name: 'Privacy Policy', href: '/pages/privacy-policy' },
      { name: 'Terms of Sales', href: '/pages/terms-of-sales' },
      { name: 'Shipping Policy', href: '/pages/shipping-policy' },
    ],
    'Knowledge Base': [
      // Intentionally left empty for now; pages can be added via CMS and linked here later.
    ],
  };

  const disallowedFooterLinkNames = new Set([
    'Product Catalog',
    'Special Offers',
    'Shipping Policy',
    'Contact Support',
    'Our Locations',
  ].map((name) => name.trim().toLowerCase()));

  // State for CMS-provided footer links
  const [mergedLinks, setMergedLinks] = useState(footerLinks);
  const [loadingCms, setLoadingCms] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadFooterPages() {
      try {
        setLoadingCms(true);
        const { data } = await api.get('/cms-pages/public', { params: { footer: '1' } });
        if (!mounted) return;

        // Create a deep copy so we don't mutate the static map
        const copy = Object.keys(footerLinks).reduce((acc, key) => {
          acc[key] = [...footerLinks[key]];
          return acc;
        }, {});

        // Group CMS pages by footer_section and push into the map (dedupe by href)
        (Array.isArray(data) ? data : []).forEach((page) => {
          const section = page.footer_section;
          // Only include pages that explicitly specify a known footer section
          if (!section || !copy[section]) return;
          const href = `/pages/${page.slug}`;
          const exists = copy[section].some(l => l.href === href);
          if (!exists) {
            copy[section].push({ name: page.title, href });
          }
        });

        // Final dedupe safeguard: ensure unique by href within each section
        Object.keys(copy).forEach((sec) => {
          const seen = new Set();
          copy[sec] = copy[sec].filter((l) => {
            const nameKey = (l.name || '').trim().toLowerCase();
            if (disallowedFooterLinkNames.has(nameKey)) {
              return false;
            }
            if (seen.has(l.href)) return false;
            seen.add(l.href);
            return true;
          });
        });

        setMergedLinks(copy);
      } catch (e) {
        // Non-blocking: if fetch fails, footer remains static
        // console.error('Failed to load CMS footer pages', e);
      } finally {
        if (mounted) setLoadingCms(false);
      }
    }
    loadFooterPages();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
      return;
    }
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const useMobileRoutes = isMobileNamespace || isMobileViewport;

  const handleFooterLinkClick = () => {
    try {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch (err) {
      // noop
    }
  };

  const isExternalHref = (href = '') => /^https?:\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');

  const normalizeInternalHref = (href = '') => {
    if (!href) return href;
    if (href.startsWith('/')) return href;
    try {
      if (typeof window !== 'undefined') {
        const url = new URL(href, window.location.origin);
        if (url.origin === window.location.origin) {
          return `${url.pathname}${url.search}${url.hash}` || '/';
        }
      }
    } catch (err) {
      // noop
    }
    return href;
  };

  const mobileLinkOverrides = {
    '/': '/mobile',
    '/makes': '/mobile/makes',
    '/accessories': '/mobile/accessories',
    '/accessories-supplies': '/mobile/accessories-supplies',
    '/parts-finder': '/mobile/parts-finder',
    '/autoglass-vin-search': '/mobile/vin-search',
    '/contact': '/mobile/contact',
    '/makes?popular=true': '/mobile/makes',
    '/watchlist': '/mobile/watchlist',
    '/account': '/mobile/accounts',
    '/login': '/mobile/login',
    '/register': '/mobile/register',
  };

  const resolveLinkHref = (category, href) => {
    const normalizedHref = normalizeInternalHref(href);
    if (!normalizedHref || isExternalHref(normalizedHref) || !useMobileRoutes) {
      return normalizedHref;
    }

    if (normalizedHref.startsWith('/mobile/')) {
      return normalizedHref;
    }

    if (normalizedHref.startsWith('/pages/')) {
      return `/mobile${normalizedHref}`;
    }

    return mobileLinkOverrides[normalizedHref] || normalizedHref;
  };

  return (
    <footer className="bg-gray-900 text-white full-bleed">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold text-sm">AGS</span>
              </div>
              <div>
                <span className="text-xl font-bold">Auto Glass Shop</span>
                <p className="text-sm text-gray-400 -mt-1">Premium Automotive Glass</p>
              </div>
            </div>
            <p className="text-gray-400 mb-4 max-w-md">
              Australia's leading supplier of premium automotive glass parts. 
              Serving professionals and enthusiasts with quality products and expert service since 2010.
            </p>
            <p className="text-gray-300 mb-6 max-w-md">
              We stock quality glass that meets Australia standard across all major makes and models. And because great
              service is in our DNA, your order is always in good hands.
            </p>
            <div className="space-y-2 text-sm text-gray-400">
              <p>📍 Head Office: Unit 9, 8 Cooper Street, Smithfield, NSW 2164</p>
              <p>📞 1300 002 345</p>
              <p>✉️ info@autoglassshop.com.au</p>
              <p>
                💬 WhatsApp: 
                <a
                  href={`https://wa.me/61435563751?text=${encodeURIComponent(`Hi Auto Glass Shop, I have a question.\nPage: ${typeof window !== 'undefined' ? window.location.pathname : ''}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-400 hover:text-primary-300 ml-1"
                >
                  +61 435 563 751
                </a>
              </p>
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(mergedLinks)
            .filter(([_, links]) => Array.isArray(links) && links.length > 0)
            .map(([category, links]) => (
            <div key={category} className={`${category === 'Knowledge Base' && links.length > 0 ? 'lg:col-span-2' : ''}`}>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                {category}
              </h3>
              <ul className="space-y-3">
                {links.map((link) => {
                  const isKnowledgeBase = category === 'Knowledge Base';
                  const targetHref = resolveLinkHref(category, link.href);
                  return (
                    <li key={`${category}-${link.href}-${link.name}`} className="break-words">
                      <Link
                        to={targetHref}
                        onClick={handleFooterLinkClick}
                        className="text-gray-400 hover:text-white text-sm transition-colors duration-200 leading-snug break-words"
                      >
                        {link.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Browse by Make — static HTML links for search engine crawlability */}
        <div className="border-t border-gray-800 mt-10 pt-8">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
            Browse Auto Glass by Vehicle Make
          </h3>
          <nav aria-label="Browse by vehicle make">
            <ul className="flex flex-wrap gap-x-4 gap-y-2">
              {(allMakes && allMakes.length > 0 ? allMakes : POPULAR_MAKES.map(name => ({ name }))).map((make) => (
                <li key={make.name || make}>
                  <Link
                    to={`/${slugify(make.name || make)}`}
                    className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
                  >
                    {make.name || make}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Browse by Make and Model — deeper internal linking for SEO */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
            Browse Auto Glass by Vehicle Make and Model
          </h3>
          <nav aria-label="Browse by vehicle make and model">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {POPULAR_MAKE_MODELS.map(({ make, models }) => (
                <div key={make}>
                  <p className="text-gray-300 text-sm font-medium mb-2">{make}</p>
                  <ul className="space-y-1">
                    {models.map((model) => (
                      <li key={model}>
                        <Link
                          to={`/${slugify(make)}/${slugify(model)}`}
                          className="text-gray-400 hover:text-white text-xs transition-colors duration-200"
                        >
                          {model}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-6 mb-4 md:mb-0">
              <p className="text-gray-400 text-sm">
                © {currentYear} Auto Glass Shop. All rights reserved.
              </p>
            </div>
            
            <div className="flex items-center space-x-6">
              {/* Payment Methods */}
              <div className="flex items-center space-x-2">
                <span className="text-gray-400 text-sm">We accept:</span>
                <div className="flex space-x-2">
                  <div className="w-8 h-5 bg-gray-700 rounded text-xs flex items-center justify-center text-gray-300">
                    VISA
                  </div>
                  <div className="w-8 h-5 bg-gray-700 rounded text-xs flex items-center justify-center text-gray-300">
                    MC
                  </div>
                  <div className="w-8 h-5 bg-gray-700 rounded text-xs flex items-center justify-center text-gray-300">
                    AMEX
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Business Hours */}
          <div className="mt-6 pt-6 border-t border-gray-800">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-400">
              <div>
                <h4 className="font-semibold text-white mb-2">Business Hours</h4>
                <p>Mon - Fri: 8:00 AM - 4:30 PM</p>
                <p>Saturday: 8:00 AM - 12:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Same Day Service</h4>
                <p>Available for metro Melbourne</p>
                <p>Order before 2:00 PM</p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Quality Auto Glass Installation</h4>
                <p>Certified technicians Recommended</p>
                <p>Fully compliant with Australian safety standards</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
